import { useEffect, useRef, useState, type RefObject } from 'react'

/**
 * «Элемент вошёл в кадр» — на голом IntersectionObserver.
 *
 * Раньше это давал framer-motion, и ради четырёх таких вызовов вся
 * библиотека (~35 КБ gzip) ехала в бандл. Чтобы не тащить её в entry,
 * две секции пришлось грузить лениво — а ленивая граница на медленной
 * сети ВЫБРАСЫВАЕТ уже отрендеренный на сервере контент и подставляет
 * пустую заглушку. Воспроизведено: при задержке чанков 400 мс блок
 * кейсов исчезал со страницы целиком (37 184 → 30 704 символов текста).
 *
 * Так что дешевле не разбивать, а не тащить.
 *
 * При prefers-reduced-motion сразу отдаём true: анимация въезда всё равно
 * отключена, а контент обязан быть видимым — в том числе в a11y-тестах,
 * которые гоняются именно с reduced motion.
 */
export function useInView(
  ref: RefObject<Element>,
  { once = true, margin = '-10%' }: { once?: boolean; margin?: string } = {},
): boolean {
  const [inView, setInView] = useState(false)
  const done = useRef(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    // Нет IO (или анимации отключены) — показываем сразу, без наблюдения.
    if (
      typeof IntersectionObserver === 'undefined' ||
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    ) {
      setInView(true)
      return
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true)
          if (once) {
            done.current = true
            io.disconnect()
          }
        } else if (!once && !done.current) {
          setInView(false)
        }
      },
      { rootMargin: margin },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [ref, once, margin])

  return inView
}
